import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Box, Layers, Loader2, Plus, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProductSearchInput } from "@/components/products/ProductSearchInput";
import { taxBadgeLabel } from "@/components/products/format";
import { useProducts } from "@/lib/api/products.queries";
import { useActiveCompany } from "@/lib/company-context";
import { cn } from "@/lib/cn";
import type {
  ProductKind,
  ProductListStatusFilter,
  ProductSummary,
} from "@/lib/api/products";

export const Route = createFileRoute("/companies/$companyId/productos/")({
  component: ProductosIndexPage,
});

type KindFilter = "all" | ProductKind;

function ProductosIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ProductosIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function ProductosIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  // Sync the URL companyId into the active context so the sidebar + header
  // reflect it. Mirrors the company detail page's behaviour.
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [statusFilter, setStatusFilter] =
    useState<ProductListStatusFilter>("active");
  const [q, setQ] = useState("");

  const filters = useMemo(
    () => ({
      ...(q ? { q } : {}),
      ...(kindFilter !== "all" ? { kind: kindFilter as ProductKind } : {}),
      status: statusFilter,
      pageSize: 50,
    }),
    [q, kindFilter, statusFilter],
  );

  const productsQuery = useProducts(companyId, filters);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {t("products.index.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("products.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("products.index.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild>
            <Link
              to="/companies/$companyId/productos/new"
              params={{ companyId }}
            >
              <Plus className="h-4 w-4" />
              {t("products.new")}
            </Link>
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <ProductSearchInput value={q} onChange={setQ} />
        <Select
          value={statusFilter}
          onValueChange={(v) =>
            setStatusFilter(v as ProductListStatusFilter)
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">{t("products.filter.active")}</SelectItem>
            <SelectItem value="archived">
              {t("products.filter.archived")}
            </SelectItem>
            <SelectItem value="all">{t("products.filter.all")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <KindFilterChips value={kindFilter} onChange={setKindFilter} />

      {productsQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : productsQuery.isError ? (
        <ErrorState
          title={t("products.index.error.title")}
          description={t("products.index.error.body")}
          onRetry={() => void productsQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : (productsQuery.data?.products.length ?? 0) === 0 ? (
        <EmptyState
          companyId={companyId}
          hasFilters={q.length > 0 || kindFilter !== "all"}
        />
      ) : (
        <ProductosTable
          companyId={companyId}
          products={productsQuery.data!.products}
        />
      )}
    </div>
  );
}

const KIND_ICON: Record<ProductKind, typeof Box> = {
  product: Box,
  service: Wrench,
  bundle: Layers,
};

function ProductosTable({
  companyId,
  products,
}: {
  companyId: string;
  products: ProductSummary[];
}) {
  const formatCurrencyCop = useMoneyFormatter();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<ProductSummary>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("products.table.name"),
        cell: ({ row }) => {
          const product = row.original;
          const KindIcon = KIND_ICON[product.kind] ?? Box;
          return (
            <div className="flex items-start gap-2">
              <KindIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <span className="block max-w-[320px] truncate font-medium">
                  {product.name}
                </span>
                {product.sku ? (
                  <span className="block max-w-[320px] truncate font-mono text-xs text-muted-foreground">
                    SKU {product.sku}
                  </span>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "kind",
        header: t("products.table.kind"),
        meta: { headClassName: "w-[130px]" },
        cell: ({ row }) => (
          <Badge variant="muted">{t(`products.kind.${row.original.kind}`)}</Badge>
        ),
      },
      {
        accessorKey: "unitPrice",
        header: t("products.table.price"),
        meta: { headClassName: "w-[170px]" },
        cell: ({ row }) => {
          const product = row.original;
          return (
            <span className="font-medium">
              {formatCurrencyCop(product.unitPrice)}
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                / {product.unitCode}
              </span>
            </span>
          );
        },
      },
      {
        id: "tax",
        header: t("products.table.tax"),
        enableSorting: false,
        meta: { headClassName: "hidden md:table-cell", cellClassName: "hidden md:table-cell" },
        cell: ({ row }) => (
          <Badge variant="muted" className="uppercase">
            {taxBadgeLabel(row.original, t)}
          </Badge>
        ),
      },
      {
        accessorKey: "status",
        header: t("products.table.status"),
        meta: { headClassName: "w-[110px]" },
        cell: ({ row }) => (
          <Badge variant={row.original.status === "active" ? "success" : "muted"}>
            {t(`products.filter.${row.original.status}`)}
          </Badge>
        ),
      },
    ],
    [formatCurrencyCop, t],
  );

  return (
    <DataTable
      columns={columns}
      data={products}
      getRowId={(product) => product.id}
      onRowClick={(product) =>
        void navigate({
          to: "/companies/$companyId/productos/$productId",
          params: { companyId, productId: product.id },
        })
      }
    />
  );
}

function KindFilterChips({
  value,
  onChange,
}: {
  value: KindFilter;
  onChange: (next: KindFilter) => void;
}) {
  const { t } = useTranslation();
  const chips: { value: KindFilter; labelKey: string }[] = [
    { value: "all", labelKey: "products.filter.all" },
    { value: "product", labelKey: "products.filter.product" },
    { value: "service", labelKey: "products.filter.service" },
    { value: "bundle", labelKey: "products.filter.bundle" },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) => {
        const active = chip.value === value;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => onChange(chip.value)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              active
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {t(chip.labelKey)}
          </button>
        );
      })}
    </div>
  );
}

function EmptyState({
  companyId,
  hasFilters,
}: {
  companyId: string;
  hasFilters: boolean;
}) {
  const { t } = useTranslation();
  if (hasFilters) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <h2 className="text-lg font-semibold">
            {t("products.empty.search")}
          </h2>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Box className="h-8 w-8 text-primary" />
        </div>
        <div className="max-w-sm space-y-1">
          <h2 className="text-lg font-semibold">{t("products.empty.title")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("products.empty.body")}
          </p>
        </div>
        <Button asChild>
          <Link
            to="/companies/$companyId/productos/new"
            params={{ companyId }}
          >
            <Plus className="h-4 w-4" />
            {t("products.new")}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
